/**
 * Evil eye tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const evilEyeTattoosPack: ToilePack = {
    slug: 'evil-eye-tattoos',
    category: 'tattoo',
    name: 'Evil eye tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'nazar', label: 'Nazar bead', subject: 'a round nazar bead of concentric rings, dark centre, light ring, dark ring and a thin outer edge'},
        {key: 'lashes', label: 'Open eye', subject: 'an almond-shaped open eye with long lashes, short rays spreading around it'},
        {key: 'moon', label: 'Eye and moon', subject: 'a small evil eye resting inside a crescent moon, three tiny stars beside it'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
    ],
    directions: ['a hamsa', 'blue ink', 'finger'],
    directionHint: 'try: a hamsa',
    seo: {
        title: 'Evil eye tattoos: nazar bead, open eye, moon',
        metaDescription: 'Nine evil eye tattoo designs, the nazar: a round bead, an open lashed eye and an eye in a crescent moon, in three styles.',
        h1: 'Evil eye tattoo designs: a nazar bead, an open eye, an eye in a moon',
        tagline: 'Three designs, each drawn in fine line, dotwork and American traditional on white paper.',
        intro: 'The evil eye, the Turkish and Greek nazar, is worn to turn away envy and harm, often small on the wrist, behind the ear or on the finger. These nine show a round nazar bead, an open eye with long lashes, and a small eye inside a crescent moon. Each is drawn as fine line, dotwork or American traditional. Type a different design or a twist, such as a hamsa beside the bead, and the three styles carry the request.',
        useCases: [
            'a small wrist, ear or finger design to take to an artist',
            'a charm drawing for a keyring or pendant',
            'a sticker for a laptop',
            'a gift card for someone who is starting a new job',
        ],
        faq: [
            {q: 'What is in the nine designs?', a: 'A round nazar bead, an open eye with long lashes and rays, and a small eye inside a crescent moon. Each one is drawn in three styles.'},
            {q: 'Can I print these?', a: 'Each design sits on a plain white background in dark line work, so it reads clearly on paper.'},
            {q: 'What is the difference between the three styles?', a: 'Fine line is one hairline stroke with almost no shading. Dotwork builds its shading from dots. American traditional has bold black outlines, heavy shading and flat red, yellow and green.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type the subject or twist you want, such as a hamsa beside the bead. The nine shown are samples, and a request is not a promised result.'},
        ],
    },
}
